"use client";

import * as PopoverPrimitive from "@radix-ui/react-popover";
import classNames from "classnames";
import dayjs from "dayjs";
import { FC, useState } from "react";
import { DayPicker } from "react-day-picker";

import CalendarIcon from "@/svgs/calendar.svg";

import { Input } from "../input";

import styles from "./datepicker.module.scss";
import { DatepickerProps } from "./datepicker.types";

// Helper function to merge the selected date with the current time
const mergeDateWithCurrentTime = (date: Date): Date => {
  const now = dayjs();
  return dayjs(date).hour(now.hour()).minute(now.minute()).second(now.second()).millisecond(now.millisecond()).toDate();
};

const Datepicker: FC<DatepickerProps> = ({
  format,
  label,
  placeholder,
  selected,
  calendarWidthFitToInput = false,
  theme = "dark",
  calendarAlign = "end",
  onSelect,
  className,
  ...daypickerProps
}) => {
  const [isOpened, setIsOpened] = useState<boolean>(false);

  return (
    <PopoverPrimitive.Root open={isOpened} onOpenChange={(newOpenedValue) => setIsOpened(newOpenedValue)}>
      <PopoverPrimitive.Trigger className={className}>
        <Input
          readOnly
          theme={theme}
          label={label}
          placeholder={placeholder}
          value={selected ? dayjs(selected).format(format) : ""}
          className={classNames(styles.trigger, isOpened && styles.opened)}
          endContent={<CalendarIcon className={styles.calendarIcon} />}
        />
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align={calendarAlign}
          sideOffset={4}
          className={classNames(styles.content, !calendarWidthFitToInput && styles.inputWidth)}
        >
          <DayPicker
            {...daypickerProps}
            showOutsideDays
            mode="single"
            captionLayout="dropdown"
            weekStartsOn={1}
            classNames={styles}
            selected={selected}
            onDayClick={() => setIsOpened(false)}
            onSelect={(date) => date && onSelect(mergeDateWithCurrentTime(date))}
          />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};

Datepicker.displayName = "Datepicker";

export { Datepicker };
